"use client";

/**
 * TierGuard Component (HUMANDA)
 * TD-014: Tier-based access control for HUMANDA portal routes
 *
 * Protects routes/components based on user tier level.
 *
 * Tier Levels:
 * - T1: Standard, Employee, Buyer
 * - T2: Team Lead, Vendor, Broker
 * - T3: Manager, Seller
 * - T4: Executive
 * - T5: Admin
 */

import type * as React from "react";
import { useAuth } from "@apal/shared-auth";

/**
 * TierGuard props interface
 */
export interface TierGuardProps {
  /** Minimum tier level required to access content (1-5) */
  minTier: number;
  /** Content to render when user has sufficient tier */
  children: React.ReactNode;
  /** Content to render when user lacks tier access (optional) */
  fallback?: React.ReactNode;
  /** Content to render while loading (optional) */
  loading?: React.ReactNode;
}

/**
 * TierGuard - Protects content based on user tier level
 *
 * @example
 * ```tsx
 * <TierGuard minTier={3} fallback={<AccessDenied />}>
 *   <SellerListings />
 * </TierGuard>
 *
 * <TierGuard minTier={5} fallback={<AccessDenied message="Admin access required." />}>
 *   <AdminDashboard />
 * </TierGuard>
 * ```
 */
export function TierGuard({
  minTier,
  children,
  fallback,
  loading,
}: TierGuardProps): React.JSX.Element | null {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Show loading state
  if (isLoading) {
    if (loading) {
      return <>{loading}</>;
    }
    return null;
  }

  // Not authenticated or no user - show fallback
  if (!isAuthenticated || !user) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return null;
  }

  // Check tier level
  const userTier = user.tierLevel ?? 0;
  if (userTier < minTier) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return null;
  }

  // User has sufficient tier - render children
  return <>{children}</>;
}

export default TierGuard;
